import { alpha, keyframes, SvgIcon, styled, type Theme } from "@mui/material";
import { type RefObject, useEffect, useRef } from "react";

const MAGIC_STAR_COUNT = 4;
const MAGIC_STAR_INTERVAL_MS = 2000;
const MAGIC_STAR_SIZE_PX = 10;
// Extra room for the diagonal of the rotating star: (10 * sqrt(2) - 10) / 2
const MAGIC_STAR_ROTATION_SLACK_PX = 3;
// How far a star may stick out of the value box (~1mm)
const MAGIC_STAR_OVERSHOOT_PX = 4;
const MAGIC_STAR_BORDER_GAP_PX = 2;
const MAGIC_STAR_PATH =
  "M512 255.1c0 11.34-7.406 20.86-18.44 23.64l-171.3 42.78l-42.78 171.1C276.7 504.6 267.2 512 255.9 512s-20.84-7.406-23.62-18.44l-42.66-171.2L18.47 279.6C7.406 276.8 0 267.3 0 255.1c0-11.34 7.406-20.83 18.44-23.61l171.2-42.78l42.78-171.1C235.2 7.406 244.7 0 256 0s20.84 7.406 23.62 18.44l42.78 171.2l171.2 42.78C504.6 235.2 512 244.6 512 255.1z";

const starScale = keyframes`
  from, to { transform: scale(0); }
  50% { transform: scale(1); }
`;

const starRotate = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

function resolveColor(palette: Theme["palette"], color?: string) {
  if (!color) return palette.accent.main;
  const [group, shade = "main"] = color.split(".");
  const entry = (palette as unknown as Record<string, unknown>)[group];
  const resolved =
    entry && typeof entry === "object"
      ? (entry as Record<string, unknown>)[shade]
      : undefined;
  return typeof resolved === "string" ? resolved : color;
}

const MagicStar = styled("span", {
  shouldForwardProp: (prop) => prop !== "starColor",
})<{ starColor?: string }>(({ theme, starColor }) => ({
  "--size": `${MAGIC_STAR_SIZE_PX}px`,
  animation: `${starScale} 700ms ease forwards`,
  display: "block",
  height: "var(--size)",
  left: "var(--star-left, 50%)",
  pointerEvents: "none",
  position: "absolute",
  top: "var(--star-top, 50%)",
  width: "var(--size)",
  "& svg": {
    animation: `${starRotate} 1000ms linear infinite`,
    display: "block",
    height: "100%",
    width: "100%",
  },
  "& svg > path": {
    fill: alpha(resolveColor(theme.palette, starColor), 0.45),
  },
  "@media (prefers-reduced-motion: reduce)": {
    display: "none",
  },
}));

function randomBetween(min: number, max: number) {
  return Math.floor(Math.random() * (max - min + 1)) + min;
}

function pickInRange(min: number, max: number) {
  return max >= min
    ? randomBetween(Math.ceil(min), Math.floor(max))
    : (min + max) / 2;
}

function restartStar(star: HTMLElement, boundary: HTMLElement | null) {
  const parent = star.offsetParent;
  if (!parent) return;

  const parentRect = parent.getBoundingClientRect();
  const boundaryRect = (boundary ?? parent).getBoundingClientRect();
  const edge = MAGIC_STAR_BORDER_GAP_PX + MAGIC_STAR_ROTATION_SLACK_PX;

  // The star may stick out of the value box by `OVERSHOOT` px but must stay at
  // least `edge` px away from the card border. Coordinates are relative to the
  // value box (offsetParent).
  const minLeft = Math.max(
    -MAGIC_STAR_OVERSHOOT_PX,
    boundaryRect.left - parentRect.left + edge,
  );
  const maxLeft = Math.min(
    parent.clientWidth - MAGIC_STAR_SIZE_PX + MAGIC_STAR_OVERSHOOT_PX,
    boundaryRect.right - parentRect.left - edge - MAGIC_STAR_SIZE_PX,
  );
  const minTop = Math.max(
    -MAGIC_STAR_OVERSHOOT_PX,
    boundaryRect.top - parentRect.top + edge,
  );
  const maxTop = Math.min(
    parent.clientHeight - MAGIC_STAR_SIZE_PX + MAGIC_STAR_OVERSHOOT_PX,
    boundaryRect.bottom - parentRect.top - edge - MAGIC_STAR_SIZE_PX,
  );

  star.style.setProperty("--star-left", `${pickInRange(minLeft, maxLeft)}px`);
  star.style.setProperty("--star-top", `${pickInRange(minTop, maxTop)}px`);

  // Reading offsetHeight forces a reflow so the CSS animation restarts
  star.style.animation = "none";
  void star.offsetHeight;
  star.style.animation = "";
}

export type MagicStarsProps = {
  /** Element whose border the stars must never get close to (the card). */
  boundaryRef: RefObject<HTMLElement | null>;
  /** Stars color. Defaults to the theme accent color. */
  color?: string;
};

/** Sparkles that jump to random positions over the nearest positioned ancestor. */
export function MagicStarsAnimation({ boundaryRef, color }: MagicStarsProps) {
  const containerRef = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    const stars = Array.from(
      containerRef.current?.children ?? [],
    ) as HTMLElement[];
    const timeouts: ReturnType<typeof setTimeout>[] = [];
    const intervals: ReturnType<typeof setInterval>[] = [];

    stars.forEach((star, index) => {
      timeouts.push(
        setTimeout(
          () => {
            restartStar(star, boundaryRef.current);
            intervals.push(
              setInterval(
                () => restartStar(star, boundaryRef.current),
                MAGIC_STAR_INTERVAL_MS,
              ),
            );
          },
          index * (MAGIC_STAR_INTERVAL_MS / 3),
        ),
      );
    });

    return () => {
      for (const timeout of timeouts) clearTimeout(timeout);
      for (const interval of intervals) clearInterval(interval);
    };
  }, [boundaryRef]);

  return (
    <span ref={containerRef} aria-hidden="true">
      {Array.from({ length: MAGIC_STAR_COUNT }, (_, index) => (
        // biome-ignore lint/suspicious/noArrayIndexKey: static list that never reorders
        <MagicStar key={index} starColor={color}>
          <SvgIcon viewBox="0 0 512 512">
            <path d={MAGIC_STAR_PATH} />
          </SvgIcon>
        </MagicStar>
      ))}
    </span>
  );
}
